import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { usePrizeBreakdown } from "../../hooks/usePrizeBreakdown";
import PrizeBreakdownControls from "./PrizeBreakdownControls";
import BowlerSummaryView from "./BowlerSummaryView";
import EventDetailView from "./EventDetailView";
import {
  sanitizeErrorMessage,
  logErrorForDebugging,
} from "../../utility/errorSanitization";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { addCurrency } from "../../utils/currencyUtils";
import { useTournamentAuth } from "../../utils/authorization";
import { getFinanceUrl } from "../../utility/adminNavigation";
import { supabase } from "../../supabaseClient";
import {
  FEATURE_CHECK_PRINTING,
  hasFeature,
} from "../../utility/features";
import ExportChecksDialog from "./ExportChecksDialog";
import ResultsCacheUpdateBanner from "../TournamentResults/components/ResultsCacheUpdateBanner";

const PrizeEmptyState = ({ title, children }) => (
  <div className="p-8 text-center">
    <h2 className="heading-display text-lg mb-2">{title}</h2>
    <p className="text-brand-muted">{children}</p>
  </div>
);

const PrizesHeader = () => (
  <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
    <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
      Prizes
    </h1>
    <p className="text-sm text-brand-mist mt-1">
      Prize money paid to each bowler.
    </p>
  </div>
);

const PrizeBreakdownView = ({ tournamentId, embedded = false }) => {
  // View mode state with responsive default
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "table";
    return window.innerWidth >= 768 ? "table" : "cards";
  });

  // Filter states
  const [searchValue, debouncedSearchValue, setSearchValue] =
    useDebouncedSearch("");
  const [filterEvent, setFilterEvent] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Data view state (summary vs detail)
  const [dataView, setDataView] = useState("summary");
  const [exportOpen, setExportOpen] = useState(false);
  const [organizationID, setOrganizationID] = useState("");
  const [hasCheckPrinting, setHasCheckPrinting] = useState(false);
  const { canManage } = useTournamentAuth(tournamentId);

  // Get data from custom hook
  const {
    prizeBreakdown,
    loading,
    isUpdating,
    isPayoutFinalized,
    isResultsFinalized,
    error,
    updateError,
    refetch,
    tournament,
  } = usePrizeBreakdown(tournamentId);

  useEffect(() => {
    if (!tournamentId) return undefined;
    if (tournament?.organizationID) {
      setOrganizationID(tournament.organizationID);
      return undefined;
    }
    let cancelled = false;
    supabase
      .from("tournaments")
      .select("organizationID")
      .eq("id", tournamentId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setOrganizationID(data?.organizationID || "");
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, tournament?.organizationID]);

  useEffect(() => {
    if (!organizationID) {
      setHasCheckPrinting(false);
      return undefined;
    }
    let cancelled = false;
    hasFeature(organizationID, FEATURE_CHECK_PRINTING)
      .then((enabled) => {
        if (!cancelled) setHasCheckPrinting(Boolean(enabled));
      })
      .catch(() => {
        if (!cancelled) setHasCheckPrinting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationID]);

  // Responsive view mode switching
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 768;
      if (isDesktop && viewMode === "cards") {
        setViewMode("table");
      } else if (!isDesktop && viewMode === "table") {
        setViewMode("cards");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [viewMode]);

  // Filter prize breakdown based on search and event filter
  const filteredBreakdown = useMemo(() => {
    try {
      return prizeBreakdown
        .filter((bowler) => {
          if (!bowler.bowlerName) return false;

          const matchesSearch = bowler.bowlerName
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase());

          const matchesEvent =
            !filterEvent ||
            bowler.events.some(
              (event) => event.eventName && event.eventName === filterEvent
            );

          return matchesSearch && matchesEvent;
        })
        .map((bowler) => {
          // If filtering by event, only include events from that event type and recalculate totals
          if (filterEvent) {
            const filteredEvents = bowler.events.filter(
              (event) => event.eventName === filterEvent
            );
            if (filteredEvents.length === 0) return null; // Skip bowlers with no events in this category

            return {
              ...bowler,
              events: filteredEvents,
              // Recalculate totals based on filtered events
              totalPrizes: filteredEvents.reduce(
                (sum, event) => addCurrency(sum, event.prize || 0),
                0
              ),
              individualPrizes: filteredEvents
                .filter((event) => event.isIndividualEvent)
                .reduce((sum, event) => addCurrency(sum, event.prize || 0), 0),
              teamPrizes: filteredEvents
                .filter((event) => !event.isIndividualEvent)
                .reduce((sum, event) => addCurrency(sum, event.prize || 0), 0),
            };
          }
          return bowler;
        })
        .filter((bowler) => bowler !== null); // Remove null entries
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "filter prize data");
      logErrorForDebugging(err, "filter prize data", sanitizedMessage);
      console.error("Filtering failed:", sanitizedMessage);
      return []; // Return empty array on error to prevent crashes
    }
  }, [prizeBreakdown, debouncedSearchValue, filterEvent]);

  // Pagination logic
  const totalItems = filteredBreakdown.length;
  const totalPages =
    itemsPerPage === "All" ? 1 : Math.ceil(totalItems / itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchValue, filterEvent]);

  // Get current page data
  const currentPageData = useMemo(() => {
    try {
      if (itemsPerPage === "All") {
        return filteredBreakdown;
      }
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filteredBreakdown.slice(startIndex, endIndex);
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "paginate prize data");
      logErrorForDebugging(err, "paginate prize data", sanitizedMessage);
      console.error("Pagination failed:", sanitizedMessage);
      return filteredBreakdown; // Return all data on error to prevent crashes
    }
  }, [filteredBreakdown, currentPage, itemsPerPage]);

  // Get unique events for filter dropdown
  const uniqueEvents = useMemo(() => {
    return [
      ...new Set(
        prizeBreakdown.flatMap((bowler) =>
          (bowler.events || [])
            .map((event) => event.eventName)
            .filter((name) => name)
        )
      ),
    ];
  }, [prizeBreakdown]);

  // Export functionality - automatically sync with current data view
  const exportToCSV = () => {
    try {
      let csvContent;

      // Use the current data view for export format
      const currentExportFormat = dataView === "summary" ? "bowler" : "event";

      if (currentExportFormat === "bowler") {
        // Export by bowler (summary view)
        csvContent = [
          [
            "Tournament Name",
            "Bowler Name",
            "Total Prizes",
            "Individual Prizes",
            "Team Prizes",
            "Events",
          ],
          ...filteredBreakdown.map((bowler) => [
            tournament?.name || "Unknown Tournament",
            bowler.bowlerName || "Unknown Bowler",
            `$${bowler.totalPrizes.toFixed(2)}`,
            `$${bowler.individualPrizes.toFixed(2)}`,
            `$${bowler.teamPrizes.toFixed(2)}`,
            bowler.events
              .map(
                (e) =>
                  `${e.eventName || "Unknown Event"} (${e.position || "N/A"})`
              )
              .join(", "),
          ]),
        ];
      } else {
        // Export by event (detailed view) - only events with prizes > $0
        csvContent = [
          [
            "Tournament Name",
            "Bowler ID",
            "Bowler Name",
            "Event Name",
            "Event ID",
            "Position",
            "Prize Amount",
            "Event Type",
            "Team ID",
            "Team Name",
          ],
          ...filteredBreakdown.flatMap((bowler) =>
            bowler.events.map((event) => [
              tournament?.name || "Unknown Tournament",
              bowler.bowlerId || "Unknown",
              bowler.bowlerName || "Unknown Bowler",
              event.eventName || "Unknown Event",
              event.eventId || "Unknown",
              event.position || "N/A",
              `$${event.prize.toFixed(2)}`,
              event.isIndividualEvent ? "Individual" : "Team",
              event.teamId || "N/A",
              event.teamName || "N/A",
            ])
          ),
        ];
      }

      const csvString = csvContent.map((row) => row.join(",")).join("\n");

      const blob = new Blob([csvString], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tournament-prizes-${currentExportFormat}-${tournamentId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "export prize data");
      logErrorForDebugging(err, "export prize data", sanitizedMessage);
      console.error("Export failed:", sanitizedMessage);
    }
  };

  if (error) {
    return (
      <div className="card-board overflow-hidden">
        {embedded ? null : <PrizesHeader />}
        <div className="p-8 text-center">
          <h3 className="heading-display text-lg mb-2">Could not load prizes</h3>
          <p className="text-brand-muted">{error}</p>
        </div>
      </div>
    );
  }

  const hasPrizes = prizeBreakdown.length > 0;
  const showUpdatingEmpty =
    isPayoutFinalized && isUpdating && !hasPrizes && !updateError;

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-brand-charcoal text-brand-cream px-4 py-2 rounded-lg z-50"
      >
        Skip to main content
      </a>
      <main id="main-content" className="w-full max-w-full" role="main">
        {(isPayoutFinalized && isUpdating) || updateError ? (
          <div className="mb-6">
            <ResultsCacheUpdateBanner
              isUpdating={isUpdating}
              updateError={updateError}
              onRetry={refetch}
              updatingLabel="Updating prize amounts"
            />
          </div>
        ) : null}

        {hasPrizes ? (
          <div className="card-board overflow-hidden mb-6">
            {embedded ? null : <PrizesHeader />}
            <div className="p-6">
              <PrizeBreakdownControls
                viewMode={viewMode}
                setViewMode={setViewMode}
                searchTerm={searchValue}
                setSearchTerm={setSearchValue}
                filterEvent={filterEvent}
                setFilterEvent={setFilterEvent}
                uniqueEvents={uniqueEvents}
                onExport={exportToCSV}
                onExportChecks={
                  canManage && hasCheckPrinting
                    ? () => setExportOpen(true)
                    : undefined
                }
                filteredCount={filteredBreakdown.length}
                totalCount={prizeBreakdown.length}
                dataView={dataView}
                setDataView={setDataView}
              />
            </div>
          </div>
        ) : (
          <div className="card-board overflow-hidden mb-6">
            {embedded ? null : <PrizesHeader />}
            {loading ? (
              <div className="p-8 text-center">
                <p className="text-brand-muted">Loading prizes.</p>
              </div>
            ) : !isPayoutFinalized ? (
              <PrizeEmptyState title="No prizes posted yet">
                Prize money shows here after payouts are locked on{" "}
                {canManage ? (
                  <Link
                    to={getFinanceUrl(tournamentId, "payouts")}
                    className="text-brand-gold-ink hover:underline"
                  >
                    Payouts
                  </Link>
                ) : (
                  "Payouts"
                )}
                .
              </PrizeEmptyState>
            ) : !isResultsFinalized ? (
              <PrizeEmptyState title="No prizes posted yet">
                Prize amounts show here after Lock Results.
              </PrizeEmptyState>
            ) : showUpdatingEmpty ? (
              <PrizeEmptyState title="Updating prizes">
                Prize amounts will show here when results finish updating.
              </PrizeEmptyState>
            ) : updateError ? (
              <PrizeEmptyState title="Could not update prizes">
                {updateError}
              </PrizeEmptyState>
            ) : (
              <PrizeEmptyState title="No prizes posted yet">
                Places are posted, but there are no prize amounts to show.
              </PrizeEmptyState>
            )}
          </div>
        )}

        {hasPrizes && loading ? (
          <BowlerSummaryView
            viewMode={viewMode}
            loading={true}
            currentPageData={[]}
            filteredBreakdown={[]}
            currentPage={1}
            totalPages={1}
            totalItems={0}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            tournamentId={tournamentId}
          />
        ) : null}
        {hasPrizes && !loading && dataView === "summary" ? (
          <BowlerSummaryView
            viewMode={viewMode}
            loading={false}
            currentPageData={currentPageData}
            filteredBreakdown={filteredBreakdown}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            tournamentId={tournamentId}
          />
        ) : null}
        {hasPrizes && !loading && dataView !== "summary" ? (
          <EventDetailView
            filteredBreakdown={filteredBreakdown}
            loading={false}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            viewMode={viewMode}
          />
        ) : null}
      </main>
      <ExportChecksDialog
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        tournamentId={tournamentId}
        organizationID={organizationID}
      />
    </>
  );
};

export default PrizeBreakdownView;
